/**
 * ChatPane — Card detail's Chat tab. Hosts:
 *   - SessionLifecycle toolbar (Start / Pause / Resume / End buttons)
 *   - Scrollable message list (user / assistant / system / tool)
 *   - Composer (textarea + dispatch options + send)
 *
 * Streaming behaviour:
 *   - The card-stream hook accumulates `token` frames into a live
 *     assistant message rendered at the bottom of the list. On `done`
 *     it's promoted into the canonical messages array.
 *   - Auto-scrolls unless the user has scrolled away from the bottom.
 *
 * Constitution check: `mode=run` is the default; `force` skips the
 * Constitution gate (founder-only — gated server-side); `dryRun` returns
 * the resolved choice without invoking the LLM. The mode selector is a
 * compact text-only dropdown next to the send button.
 *
 * Strict ADR-057: no coloured chrome. Primary "Send" is `bg-foreground
 * text-background`. The composer border is the standard hairline.
 */

import type { AgentDispatchRequest } from '@chaos/types/agent';
import type { SessionState } from '@chaos/types/session';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Send } from 'lucide-react';
import * as React from 'react';
import { type ChatMessage, useCardStream } from '../../lib/agent/use-card-stream';
import { agentApi, agentKeys, systemApi, systemKeys } from '../../lib/api';
import { cn } from '../../lib/utils';
import { FilePickerPopover } from '../files/file-picker-popover';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { SessionLifecycle } from './session-lifecycle';
import { SessionStatusBadge } from './session-status-badge';

// Lazy-load the markdown renderer — react-markdown + rehype-highlight is
// ~100 KB gz, so we only ship it when the Chat tab actually opens.
const MarkdownContent = React.lazy(() => import('./markdown-content'));

interface ChatPaneProps {
  cardId: string;
}

type DispatchMode = AgentDispatchRequest['mode'];

const MODE_LABEL: Record<NonNullable<DispatchMode>, string> = {
  run: 'Run',
  force: 'Force run',
  dryRun: 'Dry run',
};

export function ChatPane({ cardId }: ChatPaneProps) {
  const qc = useQueryClient();
  const stream = useCardStream(cardId);
  const [composer, setComposer] = React.useState('');
  const [mode, setMode] = React.useState<NonNullable<DispatchMode>>('run');
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const stuckToBottom = React.useRef(true);

  const sessionQ = useQuery({
    queryKey: agentKeys.session(cardId),
    queryFn: () => agentApi.getSessionByCard(cardId),
    refetchOnWindowFocus: true,
  });

  const loadQ = useQuery({
    queryKey: systemKeys.load(),
    queryFn: () => systemApi.load(),
    // 10s polling matches the board header.
    refetchInterval: 10_000,
    refetchOnWindowFocus: false,
  });

  // Effective state — prefer the WS push, fall back to the DB row.
  const sessionRowState = (sessionQ.data?.session?.state as SessionState | undefined) ?? 'created';
  const effectiveState: SessionState = stream.sessionState ?? sessionRowState;
  const sessionId = sessionQ.data?.session?.id ?? null;

  const [pending, setPending] = React.useState<'start' | 'pause' | 'resume' | 'end' | null>(null);
  const lifecycleM = useMutation({
    mutationFn: async (params: { action: 'start' | 'pause' | 'resume' | 'end' }) => {
      if (!sessionId) {
        // For `start` from a `created` state without an extant row, the
        // dispatch endpoint will create one on the first message. Here
        // we don't have a session_id yet — short-circuit with a
        // friendly hint via system message.
        if (params.action === 'start') {
          stream.pushSystemMessage(
            'No active session yet. Send a message to spin one up automatically.',
          );
          return null;
        }
        return null;
      }
      return agentApi.performAction(sessionId, params.action);
    },
    onMutate: (params) => {
      setPending(params.action);
    },
    onSettled: () => {
      setPending(null);
      qc.invalidateQueries({ queryKey: agentKeys.session(cardId) });
    },
  });

  const dispatchM = useMutation({
    mutationFn: (input: AgentDispatchRequest) => agentApi.dispatch(input),
    onSuccess: () => {
      // The dispatch triggered a session-row creation; refetch so the
      // lifecycle toolbar can show the right buttons.
      qc.invalidateQueries({ queryKey: agentKeys.session(cardId) });
    },
  });

  const send = async (): Promise<void> => {
    const trimmed = composer.trim();
    if (!trimmed || dispatchM.isPending) return;
    stream.pushUserMessage(trimmed);
    setComposer('');
    try {
      await dispatchM.mutateAsync({
        cardId,
        message: trimmed,
        mode,
      });
    } catch (err) {
      stream.pushSystemMessage(
        err instanceof Error ? `Error: ${err.message}` : 'Could not dispatch the message.',
      );
    }
  };

  // Auto-scroll to bottom when new content arrives and the user hasn't
  // scrolled away. The "stuck to bottom" flag flips when the user
  // manually scrolls more than ~80px above the bottom edge. We watch
  // the message count + streaming buffer length so each token nudge
  // re-runs the effect without taking the whole array reference as a
  // dependency (which would change identity on every poll).
  const messageCount = stream.messages.length;
  const streamLen = stream.streamingText.length;
  React.useEffect(() => {
    if (!stuckToBottom.current) return;
    const el = scrollRef.current;
    if (!el) return;
    // Use the trigger values so biome sees the dependency is consumed.
    // The arithmetic is a cheap no-op vs. the scroll write that follows.
    void (messageCount + streamLen);
    el.scrollTop = el.scrollHeight;
  }, [messageCount, streamLen]);

  const onScroll = React.useCallback((): void => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
    stuckToBottom.current = distanceFromBottom < 80;
  }, []);

  const loadPct = React.useMemo<number | null>(() => {
    const ram = loadQ.data?.ram.usedPct;
    const cpu = loadQ.data?.cpu.usedPct;
    if (ram === null && cpu === null) return null;
    return Math.max(ram ?? 0, cpu ?? 0);
  }, [loadQ.data]);

  return (
    <section className="flex h-full min-h-0 flex-col" aria-label="Chat with the card's agent">
      {/* Top row: session badge + lifecycle toolbar. */}
      <div className="-mx-6 px-6">
        <div className="flex items-center justify-between gap-2 pb-1">
          <SessionStatusBadge state={effectiveState} />
          {dispatchM.isPending && (
            <span className="text-[11px] text-muted-foreground">Sending…</span>
          )}
        </div>
        <SessionLifecycle
          state={effectiveState}
          pending={pending}
          loadPct={loadPct}
          onStart={() => lifecycleM.mutate({ action: 'start' })}
          onPause={() => lifecycleM.mutate({ action: 'pause' })}
          onResume={() => lifecycleM.mutate({ action: 'resume' })}
          onEnd={() => lifecycleM.mutate({ action: 'end' })}
        />
      </div>

      {/* Message list. */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 overflow-y-auto py-4"
        // Slightly negative margins to let the chat take the full width
        // of the panel without breaking the surrounding padding.
      >
        <ol className="flex flex-col gap-3">
          {stream.messages.length === 0 && !stream.streamingText && (
            <li className="px-2 py-6 text-center text-[12px] text-muted-foreground">
              No messages yet. Type below to start the conversation.
            </li>
          )}
          {stream.messages.map((m) => (
            <li key={m.id}>
              <ChatBubble message={m} />
            </li>
          ))}
          {stream.streamingText && (
            <li>
              <ChatBubble
                message={{
                  id: 'streaming',
                  role: 'assistant',
                  text: stream.streamingText,
                  at: new Date().toISOString(),
                }}
                streaming
              />
            </li>
          )}
        </ol>
      </div>

      {/* Composer. */}
      <form
        className="flex flex-col gap-2 border-t border-border pt-3"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <textarea
          value={composer}
          onChange={(e) => setComposer(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter for newlines (parity with most chat UIs).
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          rows={3}
          placeholder="Message the agent…"
          aria-label="Message"
          className={cn(
            'min-h-[72px] w-full resize-y rounded border border-border bg-background',
            'p-2 text-[13px] leading-relaxed text-foreground outline-none',
            'placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring',
          )}
        />
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <FilePickerPopover
              onPicked={(path) => {
                if (!path) return;
                // Append the path token to the composer. We use a
                // `@file:` prefix so the agent runtime can detect the
                // attachment without fragile markdown link parsing.
                const trimmed = composer.trim();
                const token = `@file:${path}`;
                setComposer(trimmed ? `${trimmed}\n${token}\n` : `${token}\n`);
              }}
            />
            <DispatchModeMenu mode={mode} onChange={setMode} />
          </div>
          <Button
            type="submit"
            size="sm"
            disabled={!composer.trim() || dispatchM.isPending}
            aria-label="Send message"
          >
            <Send className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            {dispatchM.isPending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </form>
    </section>
  );
}

function DispatchModeMenu({
  mode,
  onChange,
}: {
  mode: NonNullable<DispatchMode>;
  onChange: (m: NonNullable<DispatchMode>) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-7 items-center gap-1 rounded px-2 text-[12px]',
            'text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground',
          )}
          aria-label="Dispatch mode"
        >
          <span>{MODE_LABEL[mode]}</span>
          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Dispatch mode
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup value={mode} onValueChange={(v) => onChange(v as DispatchMode)}>
          <DropdownMenuRadioItem value="run">Run</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="force">Force run</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dryRun">Dry run</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled className="text-[11px] text-muted-foreground">
          Force skips Constitution gates (founder only)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface ChatBubbleProps {
  message: ChatMessage;
  streaming?: boolean;
}

function ChatBubble({ message, streaming }: ChatBubbleProps) {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const isAssistant = message.role === 'assistant';
  const isTool = message.role === 'tool';

  if (isSystem) {
    return <p className="px-2 text-center text-[11px] text-muted-foreground">{message.text}</p>;
  }

  if (isTool) {
    return (
      <p className="px-2 text-[11px] text-muted-foreground">
        <span className="font-mono">{message.text}</span>
      </p>
    );
  }

  return (
    <article
      className={cn(
        'rounded px-3 py-2 text-[13px] leading-relaxed',
        isUser && 'ml-6 bg-muted/60 text-foreground',
        isAssistant && 'mr-6 text-foreground',
      )}
      aria-label={isUser ? 'You' : 'Agent'}
    >
      {isUser ? (
        <p className="whitespace-pre-wrap break-words">{message.text}</p>
      ) : (
        <div className="prose prose-sm max-w-none break-words dark:prose-invert">
          <React.Suspense
            fallback={<p className="whitespace-pre-wrap break-words">{message.text}</p>}
          >
            <MarkdownContent>{message.text}</MarkdownContent>
          </React.Suspense>
          {streaming && (
            <span
              aria-hidden
              className="ml-0.5 inline-block h-3 w-[2px] animate-soft-pulse bg-foreground/70"
            />
          )}
        </div>
      )}
      {message.tokens && !streaming && (
        <p className="mt-1 text-[10px] text-muted-foreground/80">
          {message.tokens.input ?? 0} in · {message.tokens.output ?? 0} out
          {typeof message.tokens.costUsd === 'number'
            ? ` · $${message.tokens.costUsd.toFixed(4)}`
            : ''}
        </p>
      )}
    </article>
  );
}
